// ─── Support article registry ────────────────────────────────────────────────
//
// Every support article is registered here. The registry drives:
//   - The support home page (article index, grouped by category)
//   - The /support/:slug route (looks up the article by slug)
//   - Related-article suggestions within a category
//
// Adding a new article:
//   1. Create a new file in this folder, e.g. ./resetting-your-password.js
//   2. Default-export an object: { title, summary, category, body }
//   3. Import it below and add it to the ARTICLES array with a unique slug
//
// Field reference:
//   slug     - URL segment, kebab-case (e.g. 'sending-your-first-request')
//   title    - Article H1, also shown on the index card
//   summary  - One-line description for the index card
//   category - One of CATEGORIES (see below). Articles are grouped by this.
//   body     - JSX node rendered inside <SupportArticle />
//   draft    - Optional. If true, shows a "Coming soon" badge on the index
//              and renders a placeholder body instead of the real content.

import settingUpAccount         from './setting-up-your-account'
import addingFirstClient        from './adding-your-first-client'
import sendingFirstRequest      from './sending-your-first-request'
import importingTransactions    from './importing-transactions'
import readingRequestProgress   from './reading-request-progress'
import reviewingReceipts        from './reviewing-completed-receipts'
import lostReceipts             from './handling-lost-receipts'
import resendingRequest         from './resending-or-editing-a-request'
import archivingClients         from './archiving-and-reopening-clients'
import managingPracticeUsers    from './managing-practice-users'
import brandingClientEmails     from './branding-client-emails'
import changingPlan             from './changing-or-cancelling-your-plan'
import updatingPaymentMethod    from './updating-your-payment-method'
import clientDidntGetEmail      from './client-didnt-get-email'
import clientCantUpload         from './client-cant-upload'
import csvImportProblems        from './csv-import-not-working'
import errorMessages            from './common-error-messages'

// ─── Categories (display order matters) ──────────────────────────────────────

export const CATEGORIES = [
  {
    id:    'getting-started',
    label: 'Getting started',
    blurb: 'Set up your account, add your first client, and send your first request.',
  },
  {
    id:    'daily-workflow',
    label: 'Daily workflow',
    blurb: 'Working with requests, reviewing receipts, and managing clients day-to-day.',
  },
  {
    id:    'settings-billing',
    label: 'Settings and billing',
    blurb: 'Your practice users, branding, plan, and payment details.',
  },
  {
    id:    'troubleshooting',
    label: 'Troubleshooting',
    blurb: "When something isn't working as expected.",
  },
]

// ─── The registry ────────────────────────────────────────────────────────────

export const ARTICLES = [
  // Getting started
  { slug: 'setting-up-your-account',         category: 'getting-started',  ...settingUpAccount },
  { slug: 'adding-your-first-client',        category: 'getting-started',  ...addingFirstClient },
  { slug: 'sending-your-first-request',      category: 'getting-started',  ...sendingFirstRequest },
  { slug: 'importing-transactions',          category: 'getting-started',  ...importingTransactions },

  // Daily workflow
  { slug: 'reading-request-progress',        category: 'daily-workflow',   ...readingRequestProgress },
  { slug: 'reviewing-completed-receipts',    category: 'daily-workflow',   ...reviewingReceipts },
  { slug: 'handling-lost-receipts',          category: 'daily-workflow',   ...lostReceipts },
  { slug: 'resending-or-editing-a-request',  category: 'daily-workflow',   ...resendingRequest },
  { slug: 'archiving-and-reopening-clients', category: 'daily-workflow',   ...archivingClients },

  // Settings & billing
  { slug: 'managing-practice-users',         category: 'settings-billing', ...managingPracticeUsers },
  { slug: 'branding-client-emails',          category: 'settings-billing', ...brandingClientEmails },
  { slug: 'changing-or-cancelling-your-plan',category: 'settings-billing', ...changingPlan },
  { slug: 'updating-your-payment-method',    category: 'settings-billing', ...updatingPaymentMethod },

  // Troubleshooting
  { slug: 'client-didnt-get-email',          category: 'troubleshooting',  ...clientDidntGetEmail },
  { slug: 'client-cant-upload',              category: 'troubleshooting',  ...clientCantUpload },
  { slug: 'csv-import-not-working',          category: 'troubleshooting',  ...csvImportProblems },
  { slug: 'common-error-messages',           category: 'troubleshooting',  ...errorMessages },
]

// ─── Lookup helpers ──────────────────────────────────────────────────────────

export function getArticleBySlug(slug) {
  return ARTICLES.find(a => a.slug === slug) || null
}

export function getArticlesByCategory(categoryId) {
  return ARTICLES.filter(a => a.category === categoryId)
}

export function getRelatedArticles(currentSlug, max = 4) {
  const current = getArticleBySlug(currentSlug)
  if (!current) return []
  return ARTICLES
    .filter(a => a.category === current.category && a.slug !== currentSlug)
    .slice(0, max)
}
